import React, { useEffect, useState } from 'react';
import {CircularProgress, IconButton, TextareaAutosize} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import styles from './styles.module.scss';
import { correctGrammarIssues } from '@documents/sagas';

const MIN_TEXT_LENGTH = 2;
const MAX_TEXT_LENGTH = 20000;

const EnhancedTextArea = ({ value, onChange, disabled, className, ...props }) => {
  const dispatch = useDispatch();

  const [previousText, setPreviousText] = useState(undefined);
  const [isFixingGrammar, setIsFixingGrammar] = useState(false);

  const text = value ?? '';
  const isValueValidForGrammarFix = text.length >= MIN_TEXT_LENGTH && text.length <= MAX_TEXT_LENGTH;
  const isTextAreaDisabled = disabled || isFixingGrammar;
  const isGrammarFixDisabled = isTextAreaDisabled || !isValueValidForGrammarFix;
  const isReviewingCorrection = previousText !== undefined;

  useEffect(() => {
    setPreviousText(undefined);
    setIsFixingGrammar(false);
  }, [disabled]);

  const updateText = (nextValue) => {
    if (onChange) {
      onChange({
        target: {
          value: nextValue
        }
      });
    }
  };

  const handleFixGrammar = () => {
    if (isGrammarFixDisabled) return;

    const originalText = text;
    setIsFixingGrammar(true);

    dispatch(correctGrammarIssues({
      text: originalText,
      onSuccess: (data) => {
        if (data?.correctedText !== undefined) {
          setPreviousText(originalText);
          updateText(data.correctedText);
        }
        setIsFixingGrammar(false);
      },
      onError: () => {
        setIsFixingGrammar(false);
      }
    }));
  };

  const handleCancelCorrection = () => {
    updateText(previousText ?? '');
    setPreviousText(undefined);
  };

  const handleAcceptCorrection = () => {
    setPreviousText(undefined);
  };

  return (<>
    <TextareaAutosize
      {...props}
      value={value}
      onChange={onChange}
      disabled={isTextAreaDisabled}
      className={`${className ?? ''} ${styles.textArea}`.trim()}
    />
    <div className={styles.actions}>
      {isFixingGrammar ? (<>
        <CircularProgress size={15} className={styles.buttonProgress} />
      </>) : (<>
        {isReviewingCorrection ? (
          <>
            <IconButton
              className={styles.iconButton}
              onClick={handleCancelCorrection}
              disabled={disabled}
              size="small"
              aria-label="restore previous text"
            >
              <UndoIcon />
            </IconButton>
            <IconButton
              className={`${styles.iconButton} ${styles.acceptIcon}`}
              onClick={handleAcceptCorrection}
              disabled={disabled}
              size="small"
              aria-label="accept corrected text"
            >
              <CheckIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            className={styles.iconButton}
            onClick={handleFixGrammar}
            disabled={isGrammarFixDisabled}
            size="small"
            aria-label="fix grammar"
          >
            <AutoFixHighIcon />
          </IconButton>
        )}
      </>)}
      <span className={styles.actionDescription}>{!!isReviewingCorrection ? 'Improvement complete. Keep changes?' : (!!isFixingGrammar ? 'Improving text...' : 'Improve your text')}</span>
    </div>
  </>);
};

EnhancedTextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  useBorderBoxSizing: PropTypes.bool
};

export default EnhancedTextArea;
